import { Construction } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export interface NotImplementedProps {
  /** What this screen will do, in one sentence. */
  summary: string
  /** The delivery milestone it belongs to, as the phasing plan names it. */
  milestone?: string
  /** The capabilities the finished screen carries, taken from the design documents. */
  capabilities?: string[]
  className?: string
}

/**
 * The honest placeholder.
 *
 * A screen that is not built says so plainly and lists what it will do. It never shows sample
 * figures, greyed-out totals, or a chart of invented data: in a banking application, a number on
 * screen is a claim, and an operator has no way to tell a mock balance from a real one.
 */
export function NotImplemented({ summary, milestone, capabilities, className }: NotImplementedProps) {
  return (
    <Card className={cn('border-dashed', className)}>
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <Construction className="size-7 text-muted" aria-hidden />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-heading">Not yet implemented</p>
          <p className="mx-auto max-w-xl text-sm text-body">{summary}</p>
          {milestone ? <p className="text-xs text-muted">Scheduled for {milestone}.</p> : null}
        </div>

        {capabilities && capabilities.length > 0 ? (
          <ul className="mx-auto grid max-w-2xl gap-1 text-left text-xs text-muted sm:grid-cols-2">
            {capabilities.map((capability) => (
              <li key={capability} className="flex gap-2">
                <span aria-hidden className="text-brand-text">
                  ·
                </span>
                {capability}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  )
}
